import React, { Component, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { logger } from '@/services/loggingService';
import { HeadingLarge, BodyMedium, Caption } from '@/components/ui/typography';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  maxRetries?: number;
}

interface State {
  hasError: boolean;
  error?: Error;
  retryCount: number;
  isRetrying: boolean;
  isNetworkError: boolean;
}

class ChunkErrorBoundaryCore extends Component<Props & { navigate: (path: string) => void }, State> {
  private retryTimeout?: NodeJS.Timeout;

  constructor(props: Props & { navigate: (path: string) => void }) {
    super(props);
    this.state = {
      hasError: false,
      retryCount: 0,
      isRetrying: false,
      isNetworkError: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const isChunkError = error.message.includes('Loading chunk') || 
                        error.message.includes('Failed to fetch dynamically imported module') ||
                        error.message.includes('Loading CSS chunk');
    
    const isNetworkError = error.message.includes('fetch') || 
                          error.message.includes('network') ||
                          error.message.includes('ERR_NETWORK');

    return {
      hasError: true,
      error,
      isNetworkError: isChunkError || isNetworkError,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('ChunkErrorBoundary caught an error', error, {
      component: 'ChunkErrorBoundary',
      metadata: { 
        errorInfo,
        isChunkError: this.isChunkLoadingError(error),
        retryCount: this.state.retryCount,
        userAgent: navigator.userAgent,
      }
    });

    // Auto-retry for chunk loading errors
    if (this.isChunkLoadingError(error) && this.state.retryCount < (this.props.maxRetries || 3)) {
      this.scheduleRetry();
    }
  }

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  private isChunkLoadingError(error: Error): boolean {
    return error.message.includes('Loading chunk') || 
           error.message.includes('Failed to fetch dynamically imported module') ||
           error.message.includes('Loading CSS chunk') ||
           error.name === 'ChunkLoadError';
  }

  private scheduleRetry = () => {
    this.setState({ isRetrying: true });
    
    // Progressive delay: 1s, 2s, 4s
    const delay = Math.min(1000 * Math.pow(2, this.state.retryCount), 4000);
    
    this.retryTimeout = setTimeout(() => {
      this.setState(prevState => ({
        hasError: false,
        error: undefined,
        retryCount: prevState.retryCount + 1,
        isRetrying: false,
      }));
    }, delay);
  };

  private handleManualRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: undefined,
      retryCount: prevState.retryCount + 1,
      isRetrying: false,
    }));
  };

  private handleGoHome = () => {
    this.props.navigate('/workspace/talent/jobs');
  };

  private handleReload = () => {
    // Only use page reload as absolute last resort
    if (this.state.retryCount >= (this.props.maxRetries || 3)) {
      logger.warn('Max retries exceeded, using page reload as last resort', {
        component: 'ChunkErrorBoundary',
        metadata: { retryCount: this.state.retryCount }
      });
    // Use soft-first reload approach
    import('@/hooks/useNavigationHelper').then(async ({ useNavigationHelper }) => {
      const { safeReload } = useNavigationHelper();
      await safeReload('chunk-error-boundary');
    }).catch((importError) => {
      logger.warn('ChunkErrorBoundary fallback - attempting navigation recovery', {
        component: 'ChunkErrorBoundary',
        metadata: { error: String(importError) }
      });
      try {
        // Try navigation-based recovery
        window.history.replaceState(null, '', window.location.pathname);
        window.dispatchEvent(new PopStateEvent('popstate'));
      } catch (navError) {
        logger.warn('Navigation recovery failed; attempting gentle route refresh instead', {
          component: 'ChunkErrorBoundary',
          metadata: { error: String(navError) }
        });
        try {
          this.props.navigate(window.location.pathname);
        } catch (routeError) {
          logger.warn('Gentle refresh also failed; please refresh manually if needed', {
            component: 'ChunkErrorBoundary',
            metadata: { error: String(routeError) }
          });
        }
      }
    });
    } else {
      // Try navigation-based refresh first
      this.props.navigate(window.location.pathname);
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error, retryCount, isRetrying, isNetworkError } = this.state;
      const maxRetries = this.props.maxRetries || 3;
      const canRetry = retryCount < maxRetries;

      return (
        <div className="h-svh max-h-svh overflow-y-auto flex items-center justify-center bg-background p-6">
          <div className="max-w-md w-full space-y-6">
            <div className="text-center space-y-4">
              {isNetworkError ? (
                <WifiOff className="mx-auto h-12 w-12 text-warning" />
              ) : (
                <AlertCircle className="mx-auto h-12 w-12 text-destructive" />
              )}
              
              <div>
                <HeadingLarge>
                  {isRetrying ? 'Retrying...' : isNetworkError ? 'Connection Issue' : 'Loading Failed'}
                </HeadingLarge>
                <BodyMedium className="mt-2 text-muted-foreground">
                  {isRetrying ? (
                    `Attempting to reconnect... (${retryCount + 1}/${maxRetries})`
                  ) : isNetworkError ? (
                    'There seems to be a network issue. Please check your connection.'
                  ) : (
                    'Failed to load application resources. This might be due to a network issue.'
                  )}
                </BodyMedium>
              </div>

              {error && (
                <details className="text-left">
                  <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                    Technical details
                  </summary>
                  <pre className="mt-2 text-xs text-muted-foreground font-mono bg-muted p-2 rounded text-left overflow-auto">
                    {error.message}
                  </pre>
                </details>
              )}
            </div>

            <div className="space-y-2">
              {canRetry ? (
                <Button 
                  onClick={this.handleManualRetry}
                  disabled={isRetrying}
                  className="w-full"
                >
                  {isRetrying ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      Retrying...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Try Again ({retryCount}/{maxRetries})
                    </>
                  )}
                </Button>
              ) : (
                <Button 
                  onClick={this.handleReload}
                  className="w-full"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {this.state.retryCount >= (this.props.maxRetries || 3) ? 'Reload Page' : 'Refresh Route'}
                </Button>
              )}
              
              <Button 
                variant="outline"
                onClick={this.handleGoHome}
                className="w-full"
              >
                Go to Dashboard
              </Button>

              {isNetworkError && (
                <Caption className="flex items-center justify-center">
                  <Wifi className="mr-1 h-3 w-3" />
                  <span>Check your internet connection</span>
                </Caption>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// HOC to provide navigate function
export function ChunkErrorBoundary(props: Props) {
  const navigate = useNavigate();
  return <ChunkErrorBoundaryCore {...props} navigate={navigate} />;
}